import { useEffect, useState } from "react";
import { Plus, Pencil, School, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, formatRupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const emptyForm = { name: "", address: "", spp_nominal: "" };

export default function Schools() {
  const { user, schoolId, setSchoolId } = useAuth();
  const [schools, setSchools] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    api.get("/schools").then((r) => {
      setSchools(r.data);
      if (!schoolId && r.data.length > 0) setSchoolId(r.data[0].id);
    }).catch((e) => toast.error(apiError(e)));
  };

  useEffect(load, []);

  if (user.role !== "super_admin") {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-12 text-center" data-testid="schools-forbidden">
        <p className="text-sm text-slate-500">Halaman ini hanya untuk Super Admin.</p>
      </div>
    );
  }

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, address: s.address || "", spp_nominal: String(s.spp_nominal || "") });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { name: form.name, address: form.address, spp_nominal: Number(form.spp_nominal) || 0 };
    try {
      if (editing) {
        await api.put(`/schools/${editing.id}`, payload);
        toast.success("Data sekolah diperbarui");
      } else {
        const r = await api.post("/schools", payload);
        toast.success("Sekolah baru ditambahkan");
        if (!schoolId) setSchoolId(r.data.id);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (s) => {
    try {
      await api.post(`/schools/${s.id}/restore`);
      toast.success(`${s.name} dipulihkan dari arsip`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="schools-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Sekolah</h1>
          <p className="mt-1 text-sm text-slate-500">Kelola sekolah yang terdaftar di KasPintar.</p>
        </div>
        <Button onClick={openAdd} data-testid="add-school-button" className="gap-2 rounded-md bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
          <Plus className="h-4 w-4" strokeWidth={1.5} /> Tambah Sekolah
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="overflow-x-auto">
          <Table data-testid="schools-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nama Sekolah</TableHead>
                <TableHead>Alamat</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Nominal SPP / Bulan</TableHead>
                <TableHead className="text-right">Siswa</TableHead>
                <TableHead className="text-right">Staf</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools === null && (
                <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {schools !== null && schools.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center">
                    <School className="mx-auto mb-2 h-8 w-8 text-slate-300" strokeWidth={1.5} />
                    <p className="text-sm text-slate-500">Belum ada sekolah. Klik "Tambah Sekolah" untuk memulai.</p>
                  </TableCell>
                </TableRow>
              )}
              {(schools || []).map((s) => (
                <TableRow key={s.id} className={`transition-colors duration-200 hover:bg-slate-50 ${s.active === false ? "opacity-60" : ""}`} data-testid={`school-row-${s.id}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{s.name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{s.address || "-"}</TableCell>
                  <TableCell>
                    <Badge className={`rounded-full ${s.active === false ? "bg-slate-100 text-slate-500 hover:bg-slate-100" : "bg-emerald-50 text-emerald-700 hover:bg-emerald-50"}`} data-testid={`school-status-${s.id}`}>
                      {s.active === false ? "Arsip" : "Aktif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium text-slate-900">{formatRupiah(s.spp_nominal)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-600">{s.student_count}</TableCell>
                  <TableCell className="text-right text-sm text-slate-600">{s.staff_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-school-${s.id}`}>
                        <Pencil className="h-4 w-4 text-slate-500" strokeWidth={1.5} />
                      </Button>
                      {s.active === false ? (
                        <Button variant="ghost" size="icon" title="Pulihkan dari arsip" onClick={() => handleRestore(s)} data-testid={`restore-school-${s.id}`}>
                          <RotateCcw className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="icon" title="Arsipkan (data tetap tersimpan)" onClick={() => setDeleting(s)} data-testid={`delete-school-${s.id}`}>
                          <Archive className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} data-testid="school-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Ubah Data Sekolah" : "Tambah Sekolah Baru"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="s-name">Nama Sekolah</Label>
              <Input id="s-name" data-testid="school-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Contoh: SDN 1 Sukamaju" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-address">Alamat</Label>
              <Input id="s-address" data-testid="school-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Alamat sekolah (opsional)" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-spp">Nominal SPP per Bulan (Rp)</Label>
              <Input id="s-spp" type="number" min="0" data-testid="school-spp-input" value={form.spp_nominal} onChange={(e) => setForm({ ...form, spp_nominal: e.target.value })} required placeholder="Contoh: 150000" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="school-form-cancel">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="school-form-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title="Arsipkan Sekolah"
        description={`${deleting?.name} akan dinonaktifkan dan disembunyikan. SEMUA data (siswa, SPP, tabungan, tagihan) TETAP TERSIMPAN, cadangan otomatis dibuat, dan sekolah bisa dipulihkan kapan saja dari halaman ini.`}
        onConfirm={async () => {
          try {
            await api.delete(`/schools/${deleting.id}`);
            toast.success("Sekolah diarsipkan — data aman & bisa dipulihkan");
            setDeleting(null);
            load();
          } catch (err) {
            toast.error(apiError(err));
          }
        }}
        testId="confirm-delete-school"
      />
    </div>
  );
}
